import { lazy, Suspense, type JSX } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';

import { App } from './App';
import { LoadingSpinner } from './components/LoadingSpinner';
import { PrequeueGuard } from './components/prequeue/PrequeueGuard';
import { RequireAuth } from './components/auth/RequireAuth';

const LandingPage = lazy(() => import('./pages/LandingPage').then((m) => ({ default: m.LandingPage })));
const MatchPage = lazy(() => import('./pages/MatchPage').then((m) => ({ default: m.MatchPage })));
const PreQueueSetupPage = lazy(() =>
  import('./pages/PreQueueSetupPage').then((m) => ({ default: m.PreQueueSetupPage })),
);
const LegalPage = lazy(() => import('./pages/LegalPage').then((m) => ({ default: m.LegalPage })));
const AccessDeniedPage = lazy(() =>
  import('./pages/AccessDeniedPage').then((m) => ({ default: m.AccessDeniedPage })),
);
const AgeRestrictedPage = lazy(() =>
  import('./pages/AgeRestrictedPage').then((m) => ({ default: m.AgeRestrictedPage })),
);
const TosPage = lazy(() => import('./pages/TosPage').then((m) => ({ default: m.TosPage })));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage').then((m) => ({ default: m.PrivacyPage })));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage').then((m) => ({ default: m.NotFoundPage })));
const AgeVerificationFlow = lazy(() =>
  import('./pages/AgeVerificationFlow').then((m) => ({ default: m.AgeVerificationFlow })),
);
const AppMainPage = lazy(() => import('./pages/AppMainPage').then((m) => ({ default: m.AppMainPage })));
const AppealFormPage = lazy(() => import('./pages/AppealFormPage').then((m) => ({ default: m.AppealFormPage })));
// B14 friends-network
const FriendsPage = lazy(() => import('./pages/FriendsPage').then((m) => ({ default: m.FriendsPage })));
const FriendChatPage = lazy(() => import('./pages/FriendChatPage').then((m) => ({ default: m.FriendChatPage })));
// B14b penpal polish
const PenpalChatPage = lazy(() => import('./pages/PenpalChatPage').then((m) => ({ default: m.PenpalChatPage })));
// B15 notifications-system
const NotificationSettingsPage = lazy(() =>
  import('./pages/NotificationSettingsPage').then((m) => ({ default: m.NotificationSettingsPage })),
);
// B16 user-profile-account
const AccountPage = lazy(() => import('./pages/AccountPage').then((m) => ({ default: m.AccountPage })));
const ProfilePage = lazy(() => import('./pages/ProfilePage').then((m) => ({ default: m.ProfilePage })));
// B18 seo-foundation — public content pages
const AboutPage = lazy(() => import('./pages/AboutPage').then((m) => ({ default: m.AboutPage })));
const FaqPage = lazy(() => import('./pages/FaqPage').then((m) => ({ default: m.FaqPage })));
const TransparencyPage = lazy(() => import('./pages/TransparencyPage').then((m) => ({ default: m.TransparencyPage })));
const ContactDpoPage = lazy(() => import('./pages/ContactDpoPage').then((m) => ({ default: m.ContactDpoPage })));

function withSuspense(node: JSX.Element): JSX.Element {
  return <Suspense fallback={<LoadingSpinner fullScreen />}>{node}</Suspense>;
}

// react-router-dom 7+: future flags from v6 are now default; no `future` option needed.
// B18 i18n: path-based locale via router basename. MVP locale = `en`; all routes
// live under `/en/*`. A non-`/en` entry path is redirected to `/en/...` in main.tsx
// (+ Apache 301 in prod). Future locales switch basename to the detected segment.
export const ROUTER_BASENAME = '/en';

export const router = createBrowserRouter([
  {
    path: '/',
    element: <App />,
    children: [
      { index: true, element: withSuspense(<LandingPage />) },
      {
        path: 'match',
        element: (
          <RequireAuth>
            <PrequeueGuard>{withSuspense(<MatchPage />)}</PrequeueGuard>
          </RequireAuth>
        ),
      },
      { path: 'onboarding', element: <RequireAuth>{withSuspense(<AgeVerificationFlow />)}</RequireAuth> },
      { path: 'app', element: <RequireAuth>{withSuspense(<AppMainPage />)}</RequireAuth> },
      { path: 'app/setup', element: <RequireAuth>{withSuspense(<PreQueueSetupPage />)}</RequireAuth> },
      { path: 'legal', element: withSuspense(<LegalPage />) },
      { path: 'access-denied', element: withSuspense(<AccessDeniedPage />) },
      { path: 'age-restricted', element: withSuspense(<AgeRestrictedPage />) },
      { path: 'tos', element: withSuspense(<TosPage />) },
      { path: 'privacy', element: withSuspense(<PrivacyPage />) },
      // B18 seo-foundation — public content pages
      { path: 'about', element: withSuspense(<AboutPage />) },
      { path: 'faq', element: withSuspense(<FaqPage />) },
      { path: 'transparency', element: withSuspense(<TransparencyPage />) },
      { path: 'contact-dpo', element: withSuspense(<ContactDpoPage />) },
      // B10 — UNAUTHENTICATED appeal form for banned users.
      { path: 'appeal/:token', element: withSuspense(<AppealFormPage />) },
      // B14 friends-network
      { path: 'friends', element: <RequireAuth>{withSuspense(<FriendsPage />)}</RequireAuth> },
      { path: 'friends/:friendUserId', element: <RequireAuth>{withSuspense(<FriendChatPage />)}</RequireAuth> },
      // B14b penpal polish
      { path: 'penpal/:pairingId', element: <RequireAuth>{withSuspense(<PenpalChatPage />)}</RequireAuth> },
      // B15 notifications-system
      { path: 'settings/notifications', element: <RequireAuth>{withSuspense(<NotificationSettingsPage />)}</RequireAuth> },
      // B16 user-profile-account
      { path: 'account', element: <RequireAuth>{withSuspense(<AccountPage />)}</RequireAuth> },
      { path: 'u/:nickname', element: withSuspense(<ProfilePage />) },
      { path: '404', element: withSuspense(<NotFoundPage />) },
      { path: '*', element: <Navigate to="/404" replace /> },
    ],
  },
], { basename: ROUTER_BASENAME });
